'use client';
import { generateBlockRef } from '@containers/Prismic/utils/generate-block-ref';
import {
    breakpointExtraSmall,
    breakpointLarge,
    breakpointMedium,
    breakpointSmall,
} from 'config/styles/breakpoints';
import { brandWhite } from 'config/styles/colours';
import { LinkableWrapper } from 'helpers/linkableWrapper';
import Image from 'next/image';
import PropTypes from 'prop-types';
import css from 'styled-jsx/css';
import SSRLink from '../PrismicComponents/SSRLink';

const MonumentItem = ({ block, items }) => {
    const {
        layout,
        heading,
        body_text,
        font_family,
        font_colour,
        variant_type,
        content_size,
        heading_type,
        heading_tag,
        mobile_image,
        desktop_image,
        background_colour,
    } = block;

    const splitCharacter = '^';
    const contentSplitPattern = new RegExp(/\^/i, 'ig');
    const headingMode =
        heading?.indexOf(splitCharacter) === -1 ? 'automatic' : 'manual';
    const contentMode =
        body_text?.indexOf(splitCharacter) === -1 ? 'automatic' : 'manual';
    const formattedHeading = heading?.replace(contentSplitPattern, '\n');
    const formattedBodyText = body_text?.replace(contentSplitPattern, '\n');
    const formattedLayoutName = layout?.replace(/\s+/g, '-').toLowerCase();
    const hasImages = !!desktop_image?.url && mobile_image?.url;
    const formattedFontFamily = font_family?.replace(/\s+/g, '-').split('_')[0];
    const fontWeightFromFamily = font_family
        ?.replace(/\s+/g, '-')
        .split('_')[1];
    const fontWeight = fontWeightFromFamily ? fontWeightFromFamily : 'Light';
    const variantOrderType = (variantType) =>
        variantType?.toLowerCase().replace(/ /g, '_');
    const repeatableCTA = items.map((item) => item);
    const firstCTALink = repeatableCTA?.[0]?.call_to_action_link;
    const applyLinkToWholeTileCondition = !!(
        firstCTALink && repeatableCTA?.length <= 1
    );
    const firstCTAExternalTab = repeatableCTA?.[0]?.external_tab;
    const extractFontFamily = font_family.split('_')[0];
    const stylesBlockRef = generateBlockRef();

    const spanLinkStyles = css.resolve`
        span {
            font-family: ${extractFontFamily};
            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
            ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
            color: ${font_colour ? font_colour : brandWhite};
            white-space: nowrap;
            line-height: 1.5em;
            display: inline-flex;
            text-underline-offset: 4px;
            text-decoration-thickness: 1px;
            text-decoration: underline;
            cursor: pointer;
        }
    `;

    const linkStyles = css.resolve`
        a {
            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
            ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
            color: ${font_colour ? font_colour : brandWhite};
            white-space: nowrap;
            line-height: 1.5em;
            display: inline-flex;
            text-underline-offset: 4px;
            text-decoration-thickness: 1px;
        }
    `;

    const linkableWrapper = css.resolve`
        a {
            display: block;
        }
    `;

    const getHeadingTag = (heading_type, heading_tag) => {
        if (heading_type && heading_tag) {
            return 'h1';
        }

        if (!heading_type && heading_tag) {
            return 'h2';
        }

        return 'div';
    };

    const HeadingTag = getHeadingTag(heading_type, heading_tag);

    const getFontWeight = (fontFamily) => {
        const FormattedRhymesDisplayTrailFont =
            fontFamily === 'Rhymes Display Trial' || 'Rhymes Display'
                ? 'Rhymes Display'
                : '';
        if (fontFamily === 'TWK Everett_Regular') {
            return 'font-weight: 400;';
        } else if (fontFamily === 'TWK Everett_Medium') {
            return 'font-weight: 500;';
        } else if (fontFamily === FormattedRhymesDisplayTrailFont) {
            return 'font-weight: 280;';
        } else {
            return 'font-weight: 600;';
        }
    };

    const stylingNamespace = `monumentBlockElement_${stylesBlockRef}`;
    return (
        <>
            <div
                className={`monumentBlock_element monumentBlockElement_${stylesBlockRef} ${variantOrderType(
                    variant_type
                )}`}
            >
                <LinkableWrapper
                    applyLink={applyLinkToWholeTileCondition}
                    linkUrl={firstCTALink}
                    className={linkableWrapper.className}
                    externalTab={firstCTAExternalTab}
                >
                    {desktop_image?.url && (
                        <div className="desktop_only">
                            <Image
                                src={desktop_image.url}
                                alt={`Image for ${
                                    desktop_image?.alt || heading
                                }`}
                                height={desktop_image.dimensions.height}
                                width={desktop_image.dimensions.width}
                                sizes="100vw"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: 'cover',
                                }}
                            />
                        </div>
                    )}
                    {mobile_image?.url && (
                        <div className="mobile_only">
                            <Image
                                src={mobile_image?.url}
                                alt={
                                    mobile_image?.alt
                                        ? mobile_image.alt
                                        : heading
                                        ? heading
                                        : 'Monument image'
                                }
                                height={mobile_image.dimensions.height}
                                width={mobile_image.dimensions.width}
                                sizes="100vw"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: 'cover',
                                }}
                            />
                        </div>
                    )}
                    <div
                        className={`${
                            hasImages ? 'overlay_content' : 'content_container'
                        } contentSize${content_size} ${formattedLayoutName} ${formattedFontFamily}`}
                    >
                        <div className="content">
                            {heading && (
                                <HeadingTag className="heading">
                                    {formattedHeading}
                                </HeadingTag>
                            )}
                            {formattedBodyText && (
                                <div className="body_text">
                                    {formattedBodyText}
                                </div>
                            )}
                            {items.map((item, index) => {
                                if (
                                    item?.call_to_action &&
                                    !item?.is_anchor_button
                                ) {
                                    return (
                                        item?.call_to_action && (
                                            <SSRLink
                                                asSpan={
                                                    applyLinkToWholeTileCondition
                                                        ? true
                                                        : false
                                                }
                                                linkType={
                                                    'collections' || 'products'
                                                }
                                                linkUrl={
                                                    item?.call_to_action_link
                                                }
                                                className={
                                                    applyLinkToWholeTileCondition
                                                        ? spanLinkStyles.className
                                                        : linkStyles.className
                                                }
                                                gtm_identifier={
                                                    item?.analytics_event_description
                                                }
                                                title={item?.call_to_action}
                                                key={index}
                                                externalTab={item?.external_tab}
                                            >
                                                {item?.call_to_action}
                                            </SSRLink>
                                        )
                                    );
                                }
                                if (
                                    item?.call_to_action &&
                                    item?.is_anchor_button
                                ) {
                                    return (
                                        item?.call_to_action && (
                                            <LinkableWrapper
                                                applyLink={
                                                    !applyLinkToWholeTileCondition
                                                }
                                                linkUrl={
                                                    item?.call_to_action_link
                                                }
                                                className={linkStyles.className}
                                                externalTab={
                                                    firstCTAExternalTab
                                                }
                                            >
                                                {item?.call_to_action}
                                            </LinkableWrapper>
                                        )
                                    );
                                }
                            })}
                        </div>
                    </div>
                </LinkableWrapper>
            </div>
            {linkStyles.styles}
            {spanLinkStyles.styles}
            <style>
                {`
                    .hide_for_all {
                        display: none;
                    }

                    .${stylingNamespace} {
                        flex: 1;
                        position: relative;
                        ${
                            background_colour
                                ? `background-color: ${background_colour}`
                                : ''
                        };
                        font-size: 16px; /* Reset the base font-size */
                    }

                    .monumentBlock_element .overlay_content {
                        display: flex;
                        position: absolute;
                        height: 100%;
                        width: 100%;
                        top: 0;
                        box-sizing: border-box;
                    }

                    .${stylingNamespace} .content_container {
                        display: flex;
                        height: 100%;
                        width: 100%;
                        box-sizing: border-box;
                        ${
                            background_colour
                                ? `background-color: ${background_colour}`
                                : ''
                        };
                    }

                    .${stylingNamespace} .content {
                        color: ${font_colour ? font_colour : brandWhite};
                        word-break: normal;
                    }

                    .${stylingNamespace} .heading {
                        //font-weight: bold;
                        margin: 0 auto;
                        line-height: 1em;
                        ${
                            headingMode === 'manual'
                                ? `white-space: pre-wrap`
                                : ''
                        }
                    }

                    // Defines the layout positioning
                    .${stylingNamespace} .left-top {
                        align-items: flex-start;
                        padding: 55px;
                    }

                    .${stylingNamespace} .left-bottom {
                        align-items: flex-end;
                        padding: 55px;
                    }

                    .${stylingNamespace}.left-middle {
                        align-items: center;
                        padding: 55px;
                    }

                    .${stylingNamespace} .center {
                        align-items: center;
                        justify-content: center;
                        text-align: center;
                        padding: 55px;
                    }

                    /* Acumin Font Sizes for Mobile */
                    .${stylingNamespace} .contentSizeSmall.Acumin-Pro .heading {
                        font-size: 2.8125em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .${stylingNamespace} .contentSizeMedium.Acumin-Pro .heading {
                        font-size: 3.4375em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .${stylingNamespace} .contentSizeLarge.Acumin-Pro .heading {
                        font-size: 3.75em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .${stylingNamespace} .contentSizeSmall.Acumin-Pro .body_text {
                        font-size: 0.9375em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .${stylingNamespace} .contentSizeMedium.Acumin-Pro .body_text {
                        font-size: 1.125em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .${stylingNamespace} .contentSizeLarge.Acumin-Pro .body_text {
                        font-size: 1.375em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    // Rhymes Display Font Sizes on Mobile
                    .${stylingNamespace} .contentSizeLarge.Rhymes Display .heading {
                        font-size: var(--font-size-one-serif);
                        line-height: var(--line-height-one-serif);
                        font-weight: var(--font-weight-2);
                        letter-spacing: 0.031rem;
                    }

                    .${stylingNamespace} .contentSizeMedium.Rhymes Display .heading {
                        font-size: var(--font-size-two-serif);
                        line-height: var(--line-height-two-serif);
                        font-weight: var(--font-weight-2);
                        letter-spacing: 0.031rem;
                    }

                    .${stylingNamespace} .contentSizeSmall.Rhymes Display .heading {
                        font-size: var(--font-size-three-serif);
                        line-height: var(--line-height-three-serif);
                        font-weight: var(--font-weight-2);
                        letter-spacing: 0.031rem;
                    }

                    .${stylingNamespace} .contentSizeLarge.Rhymes Display .body_text {
                        font-size: var(--font-size-one-serif);
                        line-height: var(--line-height-one-serif);
                        font-weight: var(--font-weight-2);
                        letter-spacing: 0.031rem;
                    }

                    .${stylingNamespace} .contentSizeMedium.Rhymes Display .body_text {
                        font-size: var(--font-size-two-serif);
                        line-height: var(--line-height-two-serif);
                        font-weight: var(--font-weight-2);
                        letter-spacing: 0.031rem;
                    }

                    .${stylingNamespace} .contentSizeSmall.Rhymes Display .body_text {
                        font-size: var(--font-size-three-serif);
                        line-height: var(--line-height-three-serif);
                        font-weight: var(--font-weight-2);
                        letter-spacing: 0.031rem;
                    }

                    .${stylingNamespace} .contentSizeExtra.left-top,
                    .${stylingNamespace} .left-middle,
                    .${stylingNamespace} .left-bottom,
                    .${stylingNamespace} .center {
                        padding: 30px 16px;
                    }

                    .${stylingNamespace} .contentSizeExtra .body_text {
                        ${getFontWeight(font_family)}
                        font-size: 1em;
                        line-height: 28px;
                        white-space: pre-wrap;
                    }

                    @media (min-width: 1260px) {

                        .monumentBlock_element .contentSizeExtra.left-top,
                        .monumentBlock_element .left-middle,
                        .monumentBlock_element .left-bottom,
                        .monumentBlock_element .center {
                            padding: 48px;
                        }

                        .${stylingNamespace} .contentSizeExtra .body_text {
                            ${getFontWeight(font_family)}
                            font-size: 1.25em;
                            line-height: 28px;
                            white-space: pre-wrap;
                        }

                        /* Acumin Font Sizes for Desktop */
                        .${stylingNamespace} .contentSizeSmall.Acumin-Pro .heading {
                            font-size: 4.7em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${
                                fontWeight === 'Medium'
                                    ? `font-weight: 500`
                                    : ''
                            };
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .${stylingNamespace} .contentSizeMedium.Acumin-Pro .heading {
                            font-size: 5.625em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${
                                fontWeight === 'Medium'
                                    ? `font-weight: 500`
                                    : ''
                            };
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .${stylingNamespace} .contentSizeLarge.Acumin-Pro .heading {
                            font-size: 6.875em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${
                                fontWeight === 'Medium'
                                    ? `font-weight: 500`
                                    : ''
                            };
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .${stylingNamespace} .contentSizeSmall.Acumin-Pro .body_text {
                            font-size: 1.125em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${
                                fontWeight === 'Medium'
                                    ? `font-weight: 500`
                                    : ''
                            };
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .${stylingNamespace} .contentSizeMedium.Acumin-Pro .body_text {
                            font-size: 1.375em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${
                                fontWeight === 'Medium'
                                    ? `font-weight: 500`
                                    : ''
                            };
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .${stylingNamespace} .contentSizeLarge.Acumin-Pro .body_text {
                            font-size: 1.625em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${
                                fontWeight === 'Medium'
                                    ? `font-weight: 500`
                                    : ''
                            };
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }
                    }

                    // TWK Everett Font Sizes Heading for Mobile
                    // MMDS/Title/Three
                    .monumentBlock_element .contentSizeExtra.TWK-Everett .heading {
                        font-size: var(--font-size-title3);
                        line-height: var(--line-height-title3) !important;
                    }

                    // MMDS/Title/Four
                    .monumentBlock_element .contentSizeLarge.TWK-Everett .heading {
                        font-size: var(--font-size-title4);
                        line-height: var(--line-height-title4) !important;
                    }

                    // MMDS/Title/Five
                    .monumentBlock_element .contentSizeMedium.TWK-Everett .heading {
                        font-size: var(--font-size-title5);
                        line-height: var(--line-height-title5) !important;
                    }

                    // MMDS/Title/Five
                    .monumentBlock_element .contentSizeSmall.TWK-Everett .heading {
                        font-size: var(--font-size-title5);
                        line-height: var(--line-height-title5) !important;
                    }

                    // TWK Everett Font Sizes Heading for BodyText
                    // MMDS/Copy/Two
                   .monumentBlock_element .contentSizeLarge.TWK-Everett .body_text {
                        font-size: var(--font-size-copy2);
                        line-height: var(--line-height-copy2) !important;
                    }

                    // MMDS/Copy/Three
                    .monumentBlock_element .contentSizeMedium.TWK-Everett .body_text {
                        font-size: var(--font-size-copy3);
                        line-height: var(--line-height-copy3) !important;
                    }

                    // MMDS/Copy/Three
                    .monumentBlock_element .contentSizeSmall.TWK-Everett .body_text {
                        font-size: var(--font-size-copy3);
                        line-height: var(--line-height-copy3) !important;
                    }

                    /* Monument Font sizes for mobile */
                    .monumentBlock_element .contentSizeExtra.MonumentExtended-Black .heading,
                    .monumentBlock_element .contentSizeExtra.MonumentExtended-Regular .heading {
                        font-size: 1em;
                        line-height: 1.4em;
                        padding-bottom: 20px;
                    }

                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Black .heading,
                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Regular .heading {
                        font-size: 1.875em;
                    }

                    .monumentBlock_element .contentSizeMedium.MonumentExtended-Black .heading,
                    .monumentBlock_element .contentSizeMedium.MonumentExtended-Regular .heading {
                        font-size: 2.5em;
                    }

                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Black .heading,
                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular .heading {
                        font-size: 3.125em;
                    }

                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Black .body_text,
                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Regular .body_text {
                        font-size: 0.95em;
                    }

                    .monumentBlock_element .contentSizeMedium.MonumentExtended-Black .body_text,
                    .monumentBlock_element .contentSizeMedium.MonumentExtended-Regular .body_text {
                        font-size: 1.125em;
                    }

                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Black .body_text,
                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular .body_text {
                        font-size: 1.375em;
                    }

                    @media (min-width: 1260px) {
                        // TWK Everett Font Sizes for desktop heading
                        // MMDS/Title/One
                        .monumentBlock_element .contentSizeExtra.TWK-Everett .heading {
                            font-size: var(--font-size-title1);
                            line-height: var(--line-height-title1) !important;
                        }

                        // MMDS/Title/Two
                        .monumentBlock_element .contentSizeLarge.TWK-Everett .heading {
                            font-size: var(--font-size-title2);
                            line-height: var(--line-height-title2) !important;
                        }

                        // MMDS/Title/Three
                        .monumentBlock_element .contentSizeMedium.TWK-Everett .heading {
                            font-size: var(--font-size-title3);
                            line-height: var(--line-height-title3) !important;
                        }
                        
                        .monumentBlock_element .contentSizeSmall.TWK-Everett .heading {
                            font-size: var(--font-size-title4) !important;
                            line-height: var(--line-height-title4) !important;
                        }
                        
                        // MMDS/Title/Four
                        // .monumentBlock_element .contentSizeSmall.TWK-Everett .heading {
                        //     font-size: var(--font-size-title4) !important;
                        //     line-height: var(--line-height-title4) !important;
                        // }

                        // TWK Everett Font Sizes for desktop body text
                        // MMDS/Copy/One
                        .monumentBlock_element .contentSizeLarge.TWK-Everett .body_text {
                            font-size: var(--font-size-copy1);
                            line-height: var(--line-height-copy1) !important;
                        }

                        // MMDS/Copy/Two
                        .monumentBlock_element .contentSizeMedium.TWK-Everett .body_text {
                            font-size: var(--font-size-copy2);
                            line-height: var(--line-height-copy2) !important;
                        }

                        // MMDS/Copy/Three
                       .monumentBlock_element .contentSizeSmall.TWK-Everett .body_text {
                            font-size: var(--font-size-copy3);
                            line-height: var(--line-height-copy3) !important;
                        }

                        /* Monument Font sizes for desktop */
                        .monumentBlock_element .contentSizeExtra.MonumentExtended-Black .heading,
                        .monumentBlock_element .contentSizeExtra.MonumentExtended-Regular .heading {
                            font-size: 1em;
                            padding-bottom: 20px;
                        }

                        .monumentBlock_element .contentSizeSmall.MonumentExtended-Black .heading,
                        .monumentBlock_element .contentSizeSmall.MonumentExtended-Regular .heading {
                            font-size: 2.625em;
                        }

                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Black .heading,
                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Regular .heading {
                            font-size: 3.625em;
                        }

                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Black .heading,
                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular .heading {
                            font-size: 5.625em;
                        }

                        .monumentBlock_element  .contentSizeSmall.MonumentExtended-Black .body_text,
                        .monumentBlock_element  .contentSizeSmall.MonumentExtended-Regular .body_text {
                            font-size: 1.125em;
                        }

                        .monumentBlock_element  .contentSizeMedium.MonumentExtended-Black .body_text,
                        .monumentBlock_element  .contentSizeMedium.MonumentExtended-Regular .body_text {
                            font-size: 1.375em;
                        }

                        .monumentBlock_element  .contentSizeLarge.MonumentExtended-Black .body_text,
                        .monumentBlock_element  .contentSizeLarge.MonumentExtended-Regular .body_text {
                            font-size: 1.625em;
                        }
                    }

                    .${stylingNamespace} .heading {
                        ${getFontWeight(font_family)}
                        ${
                            font_family
                                ? `font-family: ${extractFontFamily}, sans-serif;`
                                : ''
                        }
                    }

                    .${stylingNamespace} .body_text {
                        font-family: 'TWK Everett', sans-serif;
                        font-weight: 400;
                        ${
                            contentMode === 'manual'
                                ? `white-space: pre-wrap`
                                : ''
                        }
                    }

                    .${stylingNamespace} .contentSizeSmall .content .heading {
                        margin-bottom: 2px;
                        ${
                            formattedFontFamily === 'Acumin-Pro'
                                ? `margin-bottom: 10px;`
                                : 'margin-bottom: 8px;'
                        }
                    }

                    .monumentBlock_element .contentSizeSmall .content .body_text {
                        line-height: 1.5em;
                        margin: 8px 0;
                    }

                    .${stylingNamespace} .contentSizeMedium .content .heading {
                        ${
                            formattedFontFamily === 'Acumin-Pro'
                                ? `margin-bottom: 10px;`
                                : 'margin-bottom: 8px;'
                        }
                    }

                    .monumentBlock_element .contentSizeMedium .content .body_text {
                        line-height: 1.5em;
                        margin: 8px 0;
                    }

                    .${stylingNamespace} .contentSizeLarge .content .heading {
                        ${
                            formattedFontFamily === 'Acumin-Pro'
                                ? `margin-bottom: 10px;`
                                : 'margin-bottom: 8px;'
                        }
                    }

                    .monumentBlock_element .contentSizeLarge .content .body_text {
                        line-height: 1.5em;
                        margin: 8px 0;
                    }

                    @media (min-width: ${breakpointMedium}) {
                        .monumentBlock .mobile_only {
                            display: none;
                        }

                        .monumentBlock .desktop_only {
                            width: 100%;
                            display: block;
                        }
                    }

                    @media (max-width: ${breakpointMedium}) {
                        .monumentBlock .mobile_only {
                            width: 100%;
                            display: block;
                        }

                        .monumentBlock .desktop_only {
                            display: none;
                        }

                        .monumentBlock_element .left-top {
                            padding: 35px;
                        }

                        .monumentBlock_element .left-bottom {
                            padding: 35px;
                        }

                        .monumentBlock_element .left-middle {
                            padding: 35px;
                        }

                        .monumentBlock_element .center {
                            padding: 35px;
                        }
                    }

                    @media (max-width: ${breakpointSmall}) {
                        .monumentBlock_element body_text {
                            line-height: 1.6em;
                        }
                    }

                    @media (max-width: ${breakpointExtraSmall}) {
                        .monumentBlock_element .left-top,
                        .monumentBlock_element .left-middle,
                        .monumentBlock_element .left-bottom {
                            padding-left: 20px;
                        }

                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Black .heading,
                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular .heading {
                            font-size: 2.5em;
                        }

                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Black .heading,
                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Regular .heading {
                            font-size: 2.1em;
                        }

                        .monumentBlock_element .contentSizeLarge.Acumin-Pro .heading {
                            font-size: 3em;
                        }
                    }

                    .monumentBlock_element .heading > p {
                        margin: 0;
                    }

                    .monumentBlock_element .content_container.center a,
                    .monumentBlock_element .content_container.center span {
                        padding: 5px 15px 5px 15px;
                        justify-content: center;
                    }

                    .monumentBlock_element .overlay_content.center a,
                    .monumentBlock_element .overlay_content.center span {
                        padding: 5px 15px 5px 15px;
                        justify-content: center;
                    }

                    .monumentBlock_element .content_container .left-top,
                    .monumentBlock_element .left-middle,
                    .monumentBlock_element .left-bottom a,
                    .monumentBlock_element .left-bottom span {
                        padding: 5px 30px 5px 0;
                    }

                    /* Monument Font sizes for mobile */
                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Black a,
                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Black span,
                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular a,
                    .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular span {
                        font-size: 1.375em;
                    }

                    .monumentBlock_element .contentSizeMedium.MonumentExtended-Black a,
                    .monumentBlock_element .contentSizeMedium.MonumentExtended-Black span,
                    .monumentBlock_element .contentSizeMedium.MonumentExtended-Regular a,
                    .monumentBlock_element .c.monumentBlock_element ontentSizeMedium.MonumentExtended-Regular span {
                        font-size: 1.125em;
                    }

                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Black a,
                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Black span,
                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Regular a,
                    .monumentBlock_element .contentSizeSmall.MonumentExtended-Regular span {
                        font-size: 0.95em;
                    }

                    /* Acumin Font Sizes for Mobile */
                    .monumentBlock_element .contentSizeSmall.Acumin-Pro a,
                    .monumentBlock_element .contentSizeSmall.Acumin-Pro span {
                        font-size: 0.9375em;
                    }

                    .monumentBlock_element .contentSizeMedium.Acumin-Pro a,
                    .monumentBlock_element .contentSizeMedium.Acumin-Pro span {
                        font-size: 1.125em;
                    }

                    .contentSizeLarge.Acumin-Pro a,
                    .contentSizeLarge.Acumin-Pro span {
                        font-size: 1.375em;
                    }

                    .monumentBlock_element .contentSizeExtra a,
                    .monumentBlock_element .contentSizeExtra span {
                        font-family: 'Rhymes Display', sans-serif;
                        font-size: 20px;
                        font-weight: 280;
                        letter-spacing: 0.031rem;
                        line-height: 26px;
                        padding-top: 20px;
                        text-underline-offset: 4px;
                        text-decoration-thickness: 1px;
                    }

                    @media (min-width: ${breakpointMedium}) {
                        /* Monument Font sizes for desktop */
                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Black a,
                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Black span,
                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular a,
                        .monumentBlock_element .contentSizeLarge.MonumentExtended-Regular span {
                            font-size: 1.625em;
                        }

                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Black a,
                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Black span,
                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Regular a,
                        .monumentBlock_element .contentSizeMedium.MonumentExtended-Regular span {
                            font-size: 1.375em;
                        }

                        .monumentBlock_element .contentSizeSmall.MonumentExtended-Black a,
                        .monumentBlock_element .contentSizeSmall.MonumentExtended-Black span,
                        .monumentBlock_element .contentSizeSmall.MonumentExtended-Regular a,
                        .monumentBlock_element .contentSizeSmall.MonumentExtended-Regular span {
                            font-size: 1.125em;
                        }

                        /* Acumin Font Sizes for Desktop */
                        .monumentBlock_element .contentSizeSmall.Acumin-Pro a,
                        .monumentBlock_element .contentSizeSmall.Acumin-Pro span {
                            font-size: 1.125em;
                        }

                        .monumentBlock_element .contentSizeMedium.Acumin-Pro a,
                        .monumentBlock_element .contentSizeMedium.Acumin-Pro span {
                            font-size: 1.375em;
                        }

                        .monumentBlock_element .contentSizeLarge.Acumin-Pro a,
                        .monumentBlock_element .contentSizeLarge.Acumin-Pro span {
                            font-size: 1.625em;
                        }

                        .monumentBlock_element .contentSizeExtra a,
                        .monumentBlock_element .contentSizeExtra span {
                            font-family: 'Rhymes Display', sans-serif;
                            font-size: 20px;
                            font-weight: 280;
                            letter-spacing: 0.031rem;
                            line-height: 26px;
                            padding-top: 20px;
                            text-underline-offset: 4px;
                            text-decoration-thickness: 1px;
                        }
                    }
                `}
            </style>
        </>
    );
};

const MonumentBlock = ({ block, items }) => {
    if (!block || !items) return null;

    const fillRow = block?.fill_entire_row ? '1' : '2';

    return (
        <>
            <div className="monumentBlock" data-siblings-count={fillRow}>
                <MonumentItem
                    block={block}
                    items={items}
                    siblingsCount={fillRow}
                />
            </div>
            <style>
                {`
                    [data-siblings-count='1'] .content {
                        width: 40%;
                    }

                    [data-siblings-count='2'] .content {
                        width: 80%;
                    }

                    [data-siblings-count='1'] {
                        flex: 1 0 100%;
                    }

                    [data-siblings-count='2'] {
                        display: flex;
                        flex-direction: row;
                        width: 50%;
                    }

                    @media (max-width: ${breakpointLarge}) {
                        [data-siblings-count='1'] .content {
                            width: 70%;
                        }

                        [data-siblings-count='2'] .content {
                            width: 80%;
                        }
                    }

                    @media (max-width: ${breakpointSmall}) {
                        [data-siblings-count='1'] .content {
                            width: 100%;
                        }

                        [data-siblings-count='2'] .content {
                            width: 100%;
                        }

                        [data-siblings-count='2'] {
                            flex: 1 0 100%;
                        }
                    }

                    @media (max-width: ${breakpointMedium}) {
                        [data-siblings-count='2'] {
                            flex: 1 0 100%;
                        }
                    }
                `}
            </style>
            <style>
                {`
                    .monumentBlock {
                        display: flex;
                    }

                    @media (max-width: ${breakpointMedium}) {
                        .monumentBlock {
                            flex-direction: column;
                        }
                    }
                `}
            </style>
        </>
    );
};

MonumentBlock.propTypes = {
    block: PropTypes.object,
};

MonumentItem.propTypes = {
    block: PropTypes.object.isRequired,
};

export default MonumentBlock;
